import React, { useEffect, useMemo } from 'react';

import styled from 'styled-components';

import { isCommunityCCRC } from 'components/Community/utils';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';

import { PRICING_COMPARISON_COLUMN_WIDTH } from './constants';
import PricingComparisonTableBody from './PricingComparisonTableBody';
import PricingComparisonTableHeader from './PricingComparisonTableHeader';
import { PricingComparison, PricingComparisonRow } from './types';
import { formatCurrency, formatRoomTypeHeader, formatText, getDisplayDifference } from './utils';
import { WIDGET_TYPES } from '../constants';
import { getSanitizedContractLabel } from '../Graph/utils';
import { formatValueDifference, shouldDisplayValueDifference } from '../valueDifferenceUtils';
import NoDataInfo from '../Widget/NoDataContent';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const TableWrapper = styled.div<{ disableScroll?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: ${({ disableScroll }) => (disableScroll ? 'visible' : 'auto')};
  overflow-x: ${({ disableScroll }) => (disableScroll ? 'visible' : 'hidden')};
`;

const HorizontalScroll = styled.div<{ disableScroll?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: ${({ disableScroll }) => (disableScroll ? 'visible' : 'auto')};
`;

const HeaderWrapper = styled.div`
  width: 100%;
`;

const BodyWrapper = styled.div`
  width: 100%;
  overflow: visible;
`;

type Props = {
  data: PricingComparison[];
  comparisonTypeEnabled?: boolean;
  valueDisplay?: number;
};

export const PricingComparisonTable = ({ data, comparisonTypeEnabled, valueDisplay }: Props) => {
  const { setCSVData, isModalPreview } = useWidgetContext();
  const { community } = useCommunityViewContext();
  const tableData = data ?? [];
  const canShowDifferences = shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay);
  const isCCRC = isCommunityCCRC(community?.rental_types ?? []);

  const roomTypes = useMemo(() => {
    const roomTypeMap = new Map<number, string>();

    tableData.forEach((entry) => {
      (entry?.room_types ?? []).forEach((roomType) => {
        if (roomType?.room_type_id && roomType?.room_type) {
          roomTypeMap.set(roomType.room_type_id, roomType.room_type);
        }
      });
    });

    return Array.from(roomTypeMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.id - b.id);
  }, [tableData]);

  const columnCount = roomTypes.length + (isCCRC ? 5 : 4);
  const minTableWidth = columnCount * PRICING_COMPARISON_COLUMN_WIDTH + 30;

  const dataSource = useMemo<PricingComparisonRow[]>(() => {
    return tableData.map((entry, index) => {
      const roomTypeMap = (entry?.room_types ?? []).reduce<Record<number, number | null | undefined>>(
        (accumulator, roomType) => {
          if (roomType?.room_type_id) {
            accumulator[roomType.room_type_id] = roomType.community_value;
          }
          return accumulator;
        },
        {}
      );

      const roomTypeDifferenceMap = (entry?.room_types ?? []).reduce<Record<number, number | null | undefined>>(
        (accumulator, roomType) => {
          if (roomType?.room_type_id) {
            accumulator[roomType.room_type_id] = roomType.difference;
          }
          return accumulator;
        },
        {}
      );

      return {
        key: entry?.care_type_id ?? entry?.care_type ?? index,
        careType: entry?.care_type ?? null,
        roomTypeMap,
        roomTypeDifferenceMap,
        communityFee: entry?.community_fee ?? null,
        communityFeeDifference: entry?.community_fee_difference ?? null,
        feeType: entry?.care_fee_pricing_type ?? null,
        careFee: entry?.care_fee ?? null,
        careFeeDifference: entry?.care_fee_difference ?? null,
        entranceFee: entry?.entrance_fee ?? null,
        entranceFeeDifference: entry?.entrance_fee_difference ?? null,
        contractLabel: entry?.contract_label ? getSanitizedContractLabel(entry) : null,
      };
    });
  }, [tableData]);

  useEffect(() => {
    const headerRow = [
      'CARE TYPE',
      ...roomTypes.map((roomType) => formatRoomTypeHeader(roomType.name)),
      isCCRC ? 'ENTRANCE FEE' : 'COMM. FEE',
      'FEE TYPE',
      'CARE FEE',
      ...(isCCRC ? ['CONTRACT TYPE'] : []),
    ];

    const csvRows = dataSource.map((row) => {
      const roomTypeValues = roomTypes.map((roomType) => {
        const value = row.roomTypeMap[roomType.id];
        const difference = row.roomTypeDifferenceMap[roomType.id];
        const displayDifference = formatValueDifference(
          getDisplayDifference(value, difference, valueDisplay),
          WIDGET_TYPES.PRICING_COMPARISON,
          valueDisplay
        );
        const formattedValue = formatCurrency(value);
        return canShowDifferences && displayDifference ? `${formattedValue} ${displayDifference}` : formattedValue;
      });

      // Community fee
      const communityFeeDifference = formatValueDifference(
        getDisplayDifference(row.communityFee, row.communityFeeDifference, valueDisplay),
        WIDGET_TYPES.PRICING_COMPARISON,
        valueDisplay
      );
      const communityFeeValue = formatCurrency(row.communityFee);
      const communityFeeDisplay =
        canShowDifferences && communityFeeDifference
          ? `${communityFeeValue} ${communityFeeDifference}`
          : communityFeeValue;

      // Entrance fee
      const entranceFeeDifference = formatValueDifference(
        getDisplayDifference(row.entranceFee, row.entranceFeeDifference, valueDisplay),
        WIDGET_TYPES.PRICING_COMPARISON,
        valueDisplay
      );
      const entranceFeeValue = formatCurrency(row.entranceFee);
      const entranceFeeDisplay =
        canShowDifferences && entranceFeeDifference ? `${entranceFeeValue} ${entranceFeeDifference}` : entranceFeeValue;

      // Care Fees
      const careFeeDifference = formatValueDifference(
        getDisplayDifference(row.careFee, row.careFeeDifference, valueDisplay),
        WIDGET_TYPES.PRICING_COMPARISON,
        valueDisplay
      );
      const careFeeValue = formatCurrency(row.careFee);
      const careFeeDisplay =
        canShowDifferences && careFeeDifference ? `${careFeeValue} ${careFeeDifference}` : careFeeValue;

      return [
        formatText(row.careType),
        ...roomTypeValues,
        isCCRC ? entranceFeeDisplay : communityFeeDisplay,
        formatText(row.feeType),
        careFeeDisplay,
        ...(isCCRC ? [formatText(row.contractLabel)] : []),
      ];
    });

    setCSVData([headerRow, ...csvRows]);
  }, [dataSource, roomTypes, canShowDifferences, valueDisplay, setCSVData, isCCRC]);

  return (
    <Container data-testid="pc-widget-table">
      {dataSource.length === 0 && (
        <NoDataInfo data-testid="pc-widget-empty-state">No Accommmodation Data Available</NoDataInfo>
      )}
      {dataSource.length > 0 && (
        <TableWrapper
          className={isModalPreview ? undefined : 'mini-scroll'}
          disableScroll={isModalPreview}
          data-testid="pc-widget-table-wrapper"
        >
          <HorizontalScroll
            className={isModalPreview ? undefined : 'mini-scroll'}
            disableScroll={isModalPreview}
            data-testid="pc-widget-table-scroll"
          >
            <HeaderWrapper data-testid="pc-widget-header-wrapper">
              <PricingComparisonTableHeader
                roomTypes={roomTypes}
                columnCount={columnCount}
                minTableWidth={minTableWidth}
              />
            </HeaderWrapper>
            <BodyWrapper style={{ minWidth: `${minTableWidth}px` }} data-testid="pc-widget-body-wrapper">
              <PricingComparisonTableBody
                dataSource={dataSource}
                roomTypes={roomTypes}
                columnCount={columnCount}
                minTableWidth={minTableWidth}
                comparisonTypeEnabled={comparisonTypeEnabled}
                valueDisplay={valueDisplay}
              />
            </BodyWrapper>
          </HorizontalScroll>
        </TableWrapper>
      )}
    </Container>
  );
};

export default PricingComparisonTable;
